import { Reference, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ContextualMenu,
  IDragOptions,
  Modal,
  Stack,
  makeStyles,
} from '@fluentui/react';
import { showCustomToast } from 'common/components/CustomToast/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BusinessUnitModalProps } from '..';
import {
  CompanyBusinessUnitUpdate,
  CompanyBusinessUnitUpdateVariables,
} from './__generated__/CompanyBusinessUnitUpdate';
import { BasicForm } from './BasicForm';
import { Footer } from './Footer';
import { Header } from './Header';
import {
  AddressRowValues,
  BusinessUnitValues,
  CurrencyRowValues,
} from './types';
import { getDefaultValues } from './utils';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  CompanyAddressInput,
  CompanyAddressUpdateTypeInput,
  CompanyBusinessUnitCreateInput,
  CompanyBusinessUnitCurrencyInput,
  CompanyBusinessUnitCurrencyUpdateTypeInput,
  CompanyBusinessUnitUpdateInput,
  EntityDeleteInput,
} from 'common/types/globalTypes';
import { differenceBy, intersection, isEmpty } from 'lodash';
import { BusinessUnitRow } from '../../list';
import { BusinessUnitCommonData } from './__generated__/BusinessUnitCommonData';
import {
  CompanyBusinessUnitCreate,
  CompanyBusinessUnitCreateVariables,
} from './__generated__/CompanyBusinessUnitCreate';
import {
  CompanyBusinesUnitDelete,
  CompanyBusinesUnitDeleteVariables,
} from './__generated__/CompanyBusinesUnitDelete';
import {
  CompanyChartOfAccounts,
  CompanyChartOfAccountsVariables,
} from './__generated__/CompanyChartOfAccounts';
import { Addresses } from './Addresses';
import { BUSINESS_UNIT_VALUES } from './constants';
import { Currencies } from './Currencies';
import { validationSchema } from './validation';
import COMPANY_BUSINESS_UNIT_UPDATE from './CompanyBusinessUnitUpdate.graphql';
import COMPANY_BUSINESS_UNIT_CREATE from './CompanyBusinessUnitCreate.graphql';
import BUSINESS_UNIT_COMMON_DATA from './BusinessUnitCommonData.graphql';
import COMPANY_CHART_OF_ACCOUNTS from './CompanyChartOfAccounts.graphql';
import DELETE_APPROVAL from './CompanyBusinesUnitDelete.graphql';

const DragOptions: IDragOptions = {
  moveMenuItemText: 'Move',
  closeMenuItemText: 'Close',
  menu: ContextualMenu,
  dragHandleSelector: '.ms-Modal-scrollableContent > div > div:first-child ',
};

const useStyles = makeStyles(() => ({
  container: {
    width: 900,
    maxHeight: 650,
    marginTop:70,
    marginBottom:90,
    overflow:'auto'
  },
}));

import BUSINESS_UNIT_FIELDS from '../../list/BusinessUnitFields.graphql';
const BUSINESS_UNIT_FRAGMENT_NAME = 'BusinessUnitFields';

type FormModalProps = BusinessUnitModalProps & {
  onClose: () => void;
};
const fieldsToCheck = [
  'addressLine1',
  'addressLine2',
  'cityName',
  'countryId',
  'stateRegionId',
  'postalCode',
  'telephoneNumber',
  'emailAddress',
  'webAddress',
];

export const FormModal: React.FC<FormModalProps> = ({
  existingBusinessUnit,
  onClose,
}) => {
  const styles = useStyles();

  const { data: commonData } = useQuery<BusinessUnitCommonData>(
    BUSINESS_UNIT_COMMON_DATA,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );

  const [businessUnitCreate, { loading: createBusinessUnitLoading }] =
    useMutation<CompanyBusinessUnitCreate, CompanyBusinessUnitCreateVariables>(
      COMPANY_BUSINESS_UNIT_CREATE,
      { errorPolicy: 'all' }
    );

  const [updateEnvironment, { loading: updateEnvironmentLoading }] =
    useMutation<CompanyBusinessUnitUpdate, CompanyBusinessUnitUpdateVariables>(
      COMPANY_BUSINESS_UNIT_UPDATE,
      { errorPolicy: 'all' }
    );

  const formMethods = useForm<BusinessUnitValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });

  const [getCompanyChartOfAccounts, { data: companyChartOfAccountsData }] =
    useLazyQuery<CompanyChartOfAccounts, CompanyChartOfAccountsVariables>(
      COMPANY_CHART_OF_ACCOUNTS
    );

  const { handleSubmit, reset, getValues } = { ...formMethods };

  const { id, _rowTimestamp, _isUpdatable, _isDeletable } = {
    ...existingBusinessUnit,
  };

  const onHandleSubmit = async (values: BusinessUnitValues) => {
    const {
      _isProductionBusinessUnit,
      companyAddressesByCompanyIdAndEntityId,
      companyBusinessUnitCurrencies,
      ...formValues
    } = { ...values };

    if (existingBusinessUnit) {
      const defaultValues = getDefaultValues({
        existingBusinessUnit,
      });

      //Array for new addresses
      const newCompanyAddresses = companyAddressesByCompanyIdAndEntityId
        ? companyAddressesByCompanyIdAndEntityId
            ?.filter(
              (addr) =>
                !addr.id &&
                fieldsToCheck.some(
                  (key) => addr[key as keyof AddressRowValues] !== null
                )
            )
            .map(({ id, _rowTimestamp, country, stateRegion, ...addr }) => addr)
        : ([] as CompanyAddressInput[]);

      //Array for deleted addresses
      const deletedCompanyAddresses =
        defaultValues.companyAddressesByCompanyIdAndEntityId &&
        (differenceBy(
          defaultValues.companyAddressesByCompanyIdAndEntityId,
          companyAddressesByCompanyIdAndEntityId || [],
          'id'
        )
          .filter((item) => !!item.id && !!item._rowTimestamp)
          .map((addr) => ({
            id: addr.id,
            rowTimestamp: addr._rowTimestamp,
          })) as EntityDeleteInput[]);

      //Array for updated addresses
      const updatedAddresses =
        defaultValues.companyAddressesByCompanyIdAndEntityId &&
        companyAddressesByCompanyIdAndEntityId &&
        intersection(
          defaultValues.companyAddressesByCompanyIdAndEntityId.map(
            (addr) => addr.id
          ),
          companyAddressesByCompanyIdAndEntityId
            .filter((addr) => addr.id)
            .map((addr) => addr.id)
        ).reduce((arr, targetId) => {
          const initialAddress =
            defaultValues.companyAddressesByCompanyIdAndEntityId!.find(
              (addr) => addr.id === targetId
            )!;
          const { id, _rowTimestamp, country, stateRegion, ...updatedAddress } =
            companyAddressesByCompanyIdAndEntityId!.find(
              (addr) => addr.id === targetId
            )!;
          const patch = Object.entries(updatedAddress).reduce(
            (res, [key, val]) => {
              if (val !== initialAddress[key as keyof AddressRowValues])
                return { ...res, [key]: val };
              return res;
            },
            {}
          );
          if (!isEmpty(patch))
            return [
              ...arr,
              {
                id,
                rowTimestamp: _rowTimestamp,
                companyAddressPatch: patch,
              },
            ];
          return arr;
        }, [] as CompanyAddressUpdateTypeInput[]);

      //Array for business unit
      const newBusinessUnitCurrency = companyBusinessUnitCurrencies
        ? companyBusinessUnitCurrencies
            ?.filter((item) => !item.id && item.currencyId !== null)
            .map(
              ({ id, _rowTimestamp, _isUpdatable, _isDeletable, ...item }) =>
                item
            )
        : ([] as CompanyBusinessUnitCurrencyInput[]);

      //Array for deleted business unit
      const deletedBusinessUnit =
        defaultValues.companyBusinessUnitCurrencies &&
        (differenceBy(
          defaultValues.companyBusinessUnitCurrencies,
          companyBusinessUnitCurrencies || [],
          'id'
        )
          .filter((item) => !!item.id && !!item._rowTimestamp)
          .map((ele) => ({
            id: ele.id,
            rowTimestamp: ele._rowTimestamp,
          })) as EntityDeleteInput[]);

      //Array for updated addresses
      const updatedBusinessUnit =
        defaultValues.companyBusinessUnitCurrencies &&
        companyBusinessUnitCurrencies &&
        intersection(
          defaultValues.companyBusinessUnitCurrencies.map((ele) => ele.id),
          companyBusinessUnitCurrencies
            .filter((ele) => ele.id)
            .map((item) => item.id)
        ).reduce((arr, targetId) => {
          const initialBusinessUnit =
            defaultValues.companyBusinessUnitCurrencies?.find(
              (ele) => ele.id === targetId
            )!;
          const { id, _rowTimestamp, ...updatedBusinessUnit } =
            companyBusinessUnitCurrencies?.find(
              (addr) => addr.id === targetId
            )!;
          const patch = Object.entries(updatedBusinessUnit).reduce(
            (res, [key, val]) => {
              if (val !== initialBusinessUnit[key as keyof CurrencyRowValues])
                return { ...res, [key]: val };
              return res;
            },
            {}
          );
          if (!isEmpty(patch))
            return [
              ...arr,
              {
                id,
                rowTimestamp: _rowTimestamp,
                companyBusinessUnitCurrencyPatch: patch,
              },
            ];
          return arr;
        }, [] as CompanyBusinessUnitCurrencyUpdateTypeInput[]);
      const { errors } = await updateEnvironment({
        variables: {
          input: {
            id,
            rowTimestamp: _rowTimestamp,
            companyBusinessUnitPatch: {
              ...formValues,
            },
            companyAddressCreate: newCompanyAddresses,
            companyAddressDelete: deletedCompanyAddresses,
            companyAddressUpdate: updatedAddresses,
            companyBusinessUnitCurrencyCreate: newBusinessUnitCurrency,
            companyBusinessUnitCurrencyDelete: deletedBusinessUnit,
            companyBusinessUnitCurrencyUpdate: updatedBusinessUnit,
          } as CompanyBusinessUnitUpdateInput,
        },
        update: (cache, { data }) => {
          const transformedData = {
            ...data?.companyBusinessUnitUpdate?.companyBusinessUnit,
            chartOfAccount:
              data?.companyBusinessUnitUpdate?.companyBusinessUnit
                ?.companyChartOfAccount?.name || [],
          } as BusinessUnitRow;
        },
      });
      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        showCustomToast({
          content: 'Business unit updated successfully.',
          appearance: 'success',
        });
      }
    } else {
      const { errors } = await businessUnitCreate({
        variables: {
          input: {
            companyBusinessUnit: {
              ...formValues,
            },
            companyAddress: companyAddressesByCompanyIdAndEntityId
              ? companyAddressesByCompanyIdAndEntityId
                  ?.filter((addr) =>
                    fieldsToCheck.some(
                      (key) => addr[key as keyof AddressRowValues] !== null
                    )
                  )
                  .map(
                    ({ id, _rowTimestamp, country, stateRegion, ...addr }) =>
                      addr
                  )
              : ([] as CompanyAddressInput[]),
            companyBusinessUnitCurrency: companyBusinessUnitCurrencies
              ? companyBusinessUnitCurrencies
                  ?.filter((item) => item.currencyId !== null)
                  .map(
                    ({
                      id,
                      _rowTimestamp,
                      _isUpdatable,
                      _isDeletable,
                      ...item
                    }) => item
                  )
              : ([] as CompanyBusinessUnitCurrencyInput[]),
          } as CompanyBusinessUnitCreateInput,
        },

        update: (cache, { data, errors }) => {
          if (!!data?.companyBusinessUnitCreate?.companyBusinessUnit?.id) {
            cache.modify({
              fields: {
                companyBusinessUnits(existing = { nodes: [] }) {
                  const newRef = cache.writeFragment({
                    data: data?.companyBusinessUnitCreate?.companyBusinessUnit,
                    fragment: BUSINESS_UNIT_FIELDS,
                    fragmentName: BUSINESS_UNIT_FRAGMENT_NAME,
                  });

                  if (!newRef) return existing;

                  // Filter out old refs with the same id
                  const filtered = existing.nodes.filter(
                    (r: Reference) =>
                      cache.identify(r) !== cache.identify(newRef)
                  );

                  return {
                    ...existing,
                    nodes: [newRef, ...filtered],
                  };
                },
              },
            });
          }
        },
      });
      if (errors?.length) {
        onClose();
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      } else {
        onClose();
        showCustomToast({
          content: 'Business unit created successfully.',
          appearance: 'success',
        });
      }
    }
  };

  useEffect(() => {
    if (!existingBusinessUnit) {
      reset(BUSINESS_UNIT_VALUES);
    } else {
      const defaultValues = getDefaultValues({
        existingBusinessUnit,
      });
      reset(defaultValues);
    }
  }, [existingBusinessUnit, reset]);

  const fetchCompanyChartOfAccounts = () => {
    getCompanyChartOfAccounts();
  };

  const fetchCompanyChartOfAccountsMemo = useCallback(
    fetchCompanyChartOfAccounts,
    []
  );

  useEffect(() => {
    fetchCompanyChartOfAccountsMemo();
  }, [fetchCompanyChartOfAccountsMemo]);

  const { countries, companyAvailableCurrencies } = { ...commonData };
  const [deleteBusinessUnit] = useMutation<
    CompanyBusinesUnitDelete,
    CompanyBusinesUnitDeleteVariables
  >(DELETE_APPROVAL, { errorPolicy: 'all' });

  const _deleteBusinessUnit = async () => {
    if (existingBusinessUnit) {
      const { errors } = await deleteBusinessUnit({
        variables: {
          input: {
            entityDelete: [
              {
                id: existingBusinessUnit?.id,
                rowTimestamp: existingBusinessUnit?._rowTimestamp!,
              },
            ],
          },
        },
        update: (cache, { errors }) => {
          if (!errors?.length) {
            const identity = `CompanyBusinessUnit:${existingBusinessUnit.id}`;
            cache.evict({ id: identity });
            cache.gc();
          }
        },
      });
      if (errors?.length)
        showCustomToast({
          content: `${errors[0].message}`,
          appearance: 'error',
        });
      else {
        onClose();
        showCustomToast({
          content: 'Business unit deleted successfully',
          appearance: 'success',
        });
      }
    }
  };

  return (
    <Modal isOpen isBlocking dragOptions={DragOptions}>
      
        <FormProvider {...formMethods}>
          <Stack style={{overflow:'hiddens'}}>
          <Header
            onClose={onClose}
            onDelete={_deleteBusinessUnit}
            isNew={!existingBusinessUnit}
            _isDeletable={!!_isDeletable}
          />
          <Stack className={styles.container}>
          <BasicForm companyChartOfAccounts={companyChartOfAccountsData} />
          <Addresses
            isUpdatable={!existingBusinessUnit || !!_isUpdatable}
            countries={countries}
          />
          <Currencies
            isUpdatable={!existingBusinessUnit || !!_isUpdatable}
            companyCurrencies={companyAvailableCurrencies}
          />
          </Stack>
          <Footer
            loading={updateEnvironmentLoading || createBusinessUnitLoading}
            onSubmit={handleSubmit(onHandleSubmit)}
            onCancel={onClose}
          />
          </Stack>
        </FormProvider>
      
    </Modal>
  );
};
